import { Container, Dialog, DialogContent, DialogTitle } from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../server";
import { MdCategory, MdDelete, MdAdd, MdEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Loading from "../Components/global/Loading";
import { useDispatch, useSelector } from "react-redux";
import { getAllCategoriesList } from "../redux/actions/contents";
import { IoArrowBack, IoClose } from "react-icons/io5";
import { motion } from "framer-motion";
import PageName from "../Components/global/PageName";
import { GiSparkles } from "react-icons/gi";

export default function CategoryManager() {
  const dispatch = useDispatch();
  const { categoriesList, loading: categoriesLoading } = useSelector(
    (state) => state?.content
  );
  const navigate = useNavigate();
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [sync, setSync] = useState(0);

  // Edit Category Modal State
  const [editItem, setEditItem] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getAllCategoriesList(1, 100));
  }, [sync, dispatch]);

  const handleAddCategory = async (e) => {
    if (e) e.preventDefault();
    if (!category || category.trim().length === 0) {
      toast.warning("Please enter a category name.");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(`${server}/category/new`, null, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          category: category.trim(),
        },
      });
      toast.success(data?.message || "Category added successfully.");
      setCategory("");
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (cat) => {
    const userRes = window.confirm(
      `Are you sure you want to delete the category "${cat?.category}"?`
    );
    if (!userRes) return;

    try {
      setLoading(true);
      const { data } = await axios.delete(`${server}/category/deleteById`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          categoryId: cat?.id,
        },
      });
      toast.success(data?.message || "Category deleted.");
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = (cat) => {
    setEditItem(cat);
    setEditCategoryName(cat?.category || "");
    setEditModalOpen(true);
  };

  const handleUpdateCategory = async (e) => {
    if (e) e.preventDefault();

    if (!editCategoryName || editCategoryName.trim().length === 0) {
      toast.warning("Category name cannot be empty.");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.put(`${server}/category/updateById`, null, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          categoryId: editItem?.id,
          category: editCategoryName.trim(),
        },
      });
      toast.success(data?.message || "Category updated successfully.");
      setEditModalOpen(false);
      setEditItem(null);
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" className="py-4 text-[#F8F5F2]">
      <PageName
        title="Category Manager"
        subTitle="Organize portfolio galleries, services, and pricing packages in dark mode."
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-6">
        {/* Left Column: Add Category Form */}
        <div className="lg:col-span-5 xl:col-span-4">
          <div className="bg-[#121215]/90 backdrop-blur-2xl p-6 rounded-3xl border border-[#C8A165]/25 shadow-[0_15px_40px_rgba(0,0,0,0.7)] sticky top-24 text-left">
            <h3 className="text-base font-bold text-[#F8F5F2] mb-1 flex items-center gap-2">
              <GiSparkles className="text-[#C8A165]" /> Create New Category
            </h3>
            <p className="text-xs text-[#F8F5F2]/50 mb-6">
              Add classification tags to structure site content.
            </p>

            <form onSubmit={handleAddCategory} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                  Category Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Bridal Glam, Haldi, Editorial..."
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30 transition-all"
                />
              </div>

              <div className="pt-2 flex flex-col gap-2.5">
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-xs tracking-widest uppercase shadow-lg text-[#0A0A0C] transition-all disabled:opacity-50 cursor-pointer"
                  style={{
                    background: "linear-gradient(135deg, #C8A165 0%, #F5E5C9 50%, #9E7A48 100%)",
                  }}
                >
                  <MdAdd size={16} />
                  Add Category
                </motion.button>

                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-2xl border border-[#C8A165]/20 text-[#F8F5F2]/70 hover:text-[#C8A165] hover:bg-white/5 font-medium text-xs transition-colors cursor-pointer"
                >
                  <IoArrowBack size={14} /> Back
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column: Category List Cards */}
        <div className="lg:col-span-7 xl:col-span-8">
          <div className="bg-[#121215]/90 backdrop-blur-2xl p-6 md:p-7 rounded-3xl border border-[#C8A165]/25 shadow-[0_15px_40px_rgba(0,0,0,0.7)] text-left">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-[#C8A165]/15">
              <div>
                <h3 className="text-base font-bold text-[#F8F5F2] flex items-center gap-2">
                  <MdCategory className="text-[#C8A165]" /> All Active Categories
                </h3>
                <p className="text-xs text-[#F8F5F2]/50 mt-0.5">
                  Manage site content classifications.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate("/my/subCategoryManager")}
                  className="bg-[#C8A165]/10 hover:bg-[#C8A165]/20 text-[#C8A165] border border-[#C8A165]/30 px-3 py-1 rounded-full font-bold text-xs transition-all cursor-pointer flex items-center gap-1"
                >
                  <span>SubCategories</span> &rarr;
                </button>
                <span className="bg-[#C8A165]/15 text-[#C8A165] border border-[#C8A165]/30 px-3 py-1 rounded-full font-bold text-xs tracking-wider">
                  Total: {categoriesList?.length || 0}
                </span>
              </div>
            </div>

            {categoriesList?.length === 0 ? (
              <div className="text-center py-16 text-[#F8F5F2]/40 text-xs bg-[#16161C] rounded-2xl border border-dashed border-[#C8A165]/20 p-8">
                No categories created yet. Use the form on the left to add one.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {categoriesList?.map((cat, idx) => (
                  <motion.div
                    key={cat.id || idx}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: idx * 0.04 }}
                    className="p-4 rounded-2xl border border-[#C8A165]/20 hover:border-[#C8A165]/60 bg-[#16161C] flex items-center justify-between transition-all group shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#C8A165]/15 text-[#C8A165] border border-[#C8A165]/30 flex items-center justify-center font-bold text-xs shrink-0">
                        {idx + 1}
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-[#F8F5F2] capitalize group-hover:text-[#C8A165] transition-colors">
                          {cat?.category}
                        </h4>
                        <span className="text-[0.62rem] text-[#F8F5F2]/40">ID: #{cat?.id}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenEditModal(cat)}
                        className="p-2 text-[#F8F5F2]/40 hover:text-[#C8A165] hover:bg-[#C8A165]/10 rounded-xl transition-colors cursor-pointer"
                        title="Edit Category"
                      >
                        <MdEdit size={18} />
                      </button>

                      <button
                        onClick={() => handleDeleteCategory(cat)}
                        className="p-2 text-[#F8F5F2]/40 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-colors cursor-pointer"
                        title="Delete Category"
                      >
                        <MdDelete size={18} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Category Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        PaperProps={{
          style: {
            backgroundColor: "#121215",
            color: "#F8F5F2",
            borderRadius: "1.5rem",
            border: "1px solid rgba(200, 161, 101, 0.3)",
            maxWidth: "450px",
            width: "100%",
          },
        }}
      >
        <DialogTitle className="flex justify-between items-center border-b border-[#C8A165]/20 pb-3">
          <span className="text-base font-bold text-[#F8F5F2] flex items-center gap-2">
            <MdEdit className="text-[#C8A165]" /> Edit Category
          </span>
          <button
            onClick={() => setEditModalOpen(false)}
            className="text-[#F8F5F2]/50 hover:text-[#C8A165] p-1 rounded-lg transition-colors cursor-pointer"
          >
            <IoClose size={20} />
          </button>
        </DialogTitle>
        <DialogContent className="pt-4">
          <form onSubmit={handleUpdateCategory} className="space-y-4 mt-2">
            <div>
              <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                Category Name
              </label>
              <input
                type="text"
                value={editCategoryName}
                onChange={(e) => setEditCategoryName(e.target.value)}
                className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all"
              />
            </div>

            <div className="pt-3 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditModalOpen(false)}
                className="px-4 py-2 rounded-xl border border-[#C8A165]/20 text-xs font-medium text-[#F8F5F2]/70 hover:bg-white/5 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className="px-5 py-2 rounded-xl text-xs font-bold text-[#0A0A0C] tracking-wider uppercase cursor-pointer"
                style={{
                  background: "linear-gradient(135deg, #C8A165 0%, #F5E5C9 50%, #9E7A48 100%)",
                }}
              >
                Save Changes
              </motion.button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {(loading || categoriesLoading) && <Loading />}
    </Container>
  );
}
